import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import moment from "moment";

import Langs, { Localize } from "../../app/lang/langs";
import NetworkManager from "../../app/managers/networkManager";
import {
    VLearnProgressBar, VLearnStatusChip,
    formatVLearnDuration, isVLearnOverdue, vlearnSurface, vlearnType
} from "./vlearnUi";

/**
 * V-Learning — one course in the catalogue.
 *
 * The card answers two different questions depending on who is holding it. A learner needs to
 * know how far along they are and whether they are late; a manager needs to know whether the
 * course is published and how many people are on it. A course can be both — an author is
 * often also enrolled — so the two blocks are additive rather than a branch.
 *
 * ── Why the whole card is activatable ────────────────────────────────────────────────
 *
 * Opening a course is the card's primary action, and a 268-pixel-wide target that only
 * responds on its small button is a worse experience with a mouse and no better with a
 * keyboard. So the card carries the role, the tab stop and the Enter/Space handling, and the
 * buttons inside it stop propagation so that "Edit" never also means "open".
 */

const LEVEL_KEYS = {
    beginner: 'VLEARN_LEVEL_BEGINNER',
    intermediate: 'VLEARN_LEVEL_INTERMEDIATE',
    advanced: 'VLEARN_LEVEL_ADVANCED'
}

const COURSE_STATUS_KEYS = {
    draft: 'VLEARN_COURSE_STATUS_DRAFT',
    published: 'VLEARN_COURSE_STATUS_PUBLISHED',
    archived: 'VLEARN_COURSE_STATUS_ARCHIVED'
}

const COURSE_STATUS_STYLES = {
    draft: { backgroundColor: 'var(--lightGrey, #ededed)', color: '#3a3a3a' },
    published: { backgroundColor: 'rgba(58, 156, 96, 0.14)', color: '#1F7A4C' },
    archived: { backgroundColor: 'rgba(0,0,0,0.06)', color: '#6b7280' }
}

const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

/**
 * A cover for a course that has no uploaded image.
 *
 * Derived from the course id alone, so the same course always looks the same — a catalogue
 * whose placeholders reshuffle on every render is one the reader cannot navigate by shape.
 * The category's own colour is used as the first stop when it has one, which keeps a
 * category's courses visually related even without artwork.
 */
function coverGradient(idCourse, categoryColor) {
    const seed = Math.abs(Number(idCourse) || 0)
    const hue = (seed * 47) % 360
    const angle = 115 + ((seed * 23) % 110)
    const from = categoryColor || `hsl(${hue}, 56%, 50%)`
    const to = `hsl(${(hue + 46) % 360}, 44%, 32%)`
    return `linear-gradient(${angle}deg, ${from} 0%, ${to} 100%)`
}

export default function CourseCard({ course, onOpen, onEdit, scope }) {

    const open = useCallback(() => {
        if (onOpen) onOpen(course.id)
    }, [onOpen, course.id])

    const onKeyDown = useCallback((event) => {
        // Space is handled as well as Enter because the card claims `role="button"`, and a
        // button that ignores Space is not the control it says it is. The default is
        // prevented so the page does not scroll underneath the activation.
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault()
            open()
        }
    }, [open])

    /*
     * Which half of the card to show is decided by the TAB, not by the row's own flags.
     *
     * `id_enrollment` alone was the test, and an instructor is normally enrolled in their own
     * course too — they are usually a member of the team they authored it for. So a course in
     * "Courses I manage" rendered the learner half as well, offering "Start" next to "Edit".
     * The two tabs are two jobs; a card in the managing tab is about the course, not about
     * this person's progress through it.
     *
     * `scope` is optional so a caller that does not pass it keeps the old both-roles
     * behaviour rather than silently rendering nothing.
     */
    // Announcements posted in the course that this viewer has not opened yet. The server
    // reports 0 for anyone not enrolled, so this is a learner's signal only — an author does
    // not need a badge counting their own posts.
    const unreadAnnouncements = Number(course.unread_announcements) || 0

    const isLearner = !!course.id_enrollment && scope !== 'managing'
    const isManager = !!course.my_manager_role && scope !== 'learning'
    const overdue = isVLearnOverdue(course.my_due_date, course.my_status)
    const duration = formatVLearnDuration((Number(course.estimated_minutes) || 0) * 60)
    const lessons = Number(course.lessons_count) || 0
    const enrolled = Number(course.enrolled_count) || 0
    const levelKey = LEVEL_KEYS[course.level] || null
    const dueDate = course.my_due_date || course.due_date

    const coverUrl = course.cover_filename
        ? NetworkManager.getInstance().getVLearnCoverUrl(course.id, course.updated_at)
        : null

    const primaryActionKey = course.my_status === 'completed'
        ? 'VLEARN_REVIEW'
        : course.my_status === 'in_progress' ? 'VLEARN_RESUME' : 'VLEARN_START'

    return (
        <Box
            role="button"
            tabIndex={0}
            aria-label={Localize('VLEARN_OPEN_COURSE_NAMED').replace('%title%', course.title || '')}
            onClick={open}
            onKeyDown={onKeyDown}
            sx={{
                ...vlearnSurface.card,
                ...vlearnSurface.hover,
                ...vlearnSurface.reducedMotion,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                /*
                 * The card's affordance is a hover lift, which does not exist on touch — so
                 * on a touch device an active state stands in for it. `(hover: none)` is the
                 * correct query rather than a width breakpoint: it asks about the pointer,
                 * which is the actual thing that differs.
                 */
                '@media (hover: none)': {
                    '&:active': { backgroundColor: 'rgba(255, 144, 21, 0.06)' }
                },
                ...focusRing
            }}
        >
            {/* ── Cover ───────────────────────────────────────────────────────── */}
            <Box sx={{
                position: 'relative', height: 124, flexShrink: 0,
                background: coverUrl ? '#e9ebef' : coverGradient(course.id, course.category_color)
            }}>
                {coverUrl ? (
                    <Box
                        component="img"
                        src={coverUrl}
                        alt={Localize('VLEARN_COVER_ALT_NAMED').replace('%title%', course.title || '')}
                        loading="lazy"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                ) : (
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'rgba(255,255,255,0.34)', '& svg': { fontSize: 46 }
                    }} aria-hidden="true">
                        <SchoolOutlinedIcon />
                    </Box>
                )}

                <Box sx={{
                    position: 'absolute', top: 10, left: 10, right: 10,
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1
                }}>
                    {course.category_name ? (
                        <Chip
                            size="small"
                            label={course.category_name}
                            sx={{
                                maxWidth: '70%', height: 22, borderRadius: '7px',
                                fontSize: '0.7rem', fontWeight: 600,
                                backgroundColor: 'rgba(255,255,255,0.92)',
                                color: course.category_color || 'var(--text-primary, #111318)'
                            }}
                        />
                    ) : <Box />}

                    {/* Grouped, so the row keeps two sides for space-between rather than
                        three items competing for the middle. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {course.is_mandatory ? (
                            <Chip
                                size="small"
                                icon={<PriorityHighIcon sx={{ fontSize: 14 }} />}
                                label={<Langs str="VLEARN_MANDATORY" />}
                                sx={{
                                    height: 22, borderRadius: '7px', fontSize: '0.7rem', fontWeight: 700,
                                    backgroundColor: 'var(--red, rgb(251,105,98))', color: '#fff',
                                    '& .MuiChip-icon': { color: '#fff', ml: '4px', mr: '-4px' }
                                }}
                            />
                        ) : null}

                        {unreadAnnouncements > 0 ? (
                            <Tooltip
                                title={Localize(unreadAnnouncements === 1
                                    ? 'VLEARN_UNREAD_ANNOUNCEMENTS_ONE'
                                    : 'VLEARN_UNREAD_ANNOUNCEMENTS_MANY')
                                    .replace('%count%', String(unreadAnnouncements))}
                            >
                                {/* A count, not a plain dot: "there is news" is less use than
                                    "there are three things you have not read". Capped at 9+ so
                                    the badge stays a circle. */}
                                <Box
                                    role="status"
                                    aria-label={Localize(unreadAnnouncements === 1
                                        ? 'VLEARN_UNREAD_ANNOUNCEMENTS_ONE'
                                        : 'VLEARN_UNREAD_ANNOUNCEMENTS_MANY')
                                        .replace('%count%', String(unreadAnnouncements))}
                                    sx={{
                                        minWidth: 20, height: 20, px: 0.5,
                                        borderRadius: '10px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: '#ff3b30', color: '#fff',
                                        border: '2px solid #fff',
                                        fontSize: '0.68rem', fontWeight: 800, lineHeight: 1,
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
                                    }}
                                >
                                    {unreadAnnouncements > 9 ? '9+' : unreadAnnouncements}
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
            </Box>

            {/* ── Body ────────────────────────────────────────────────────────── */}
            <Box sx={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', flex: 1, gap: 1 }}>
                <Box>
                    <Typography component="h3" sx={{
                        ...vlearnType.cardTitle,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {course.title}
                    </Typography>
                    {course.subtitle ? (
                        <Typography sx={{
                            ...vlearnType.meta, mt: 0.4,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}>
                            {course.subtitle}
                        </Typography>
                    ) : null}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    {levelKey ? (
                        <Typography sx={{ ...vlearnType.meta }}><Langs str={levelKey} /></Typography>
                    ) : null}
                    {levelKey && duration ? <Dot /> : null}
                    {duration ? <Typography sx={{ ...vlearnType.meta }}>{duration}</Typography> : null}
                    {(levelKey || duration) && lessons ? <Dot /> : null}
                    {lessons ? (
                        <Typography sx={{ ...vlearnType.meta }}>
                            <Langs
                                str={lessons === 1 ? 'VLEARN_N_LESSON_ONE' : 'VLEARN_N_LESSON_MANY'}
                                params={{ count: lessons }}
                            />
                        </Typography>
                    ) : null}
                </Box>

                <Box sx={{ flex: 1 }} />

                {isLearner ? (
                    <Box>
                        <VLearnProgressBar percent={course.my_progress} status={course.my_status} />
                        <Box sx={{
                            mt: 1.25, display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', gap: 1
                        }}>
                            <VLearnStatusChip status={course.my_status} overdue={overdue} />
                            <Button
                                size="small"
                                variant="contained"
                                onClick={(event) => { event.stopPropagation(); open() }}
                                sx={{
                                    // 44px floor: this is the primary action on a card the
                                    // learner reached specifically to press it, and a
                                    // `size="small"` contained button is 30px high.
                                    ...focusRing, textTransform: 'none', fontSize: '0.78rem',
                                    minHeight: 44, boxShadow: 'none',
                                    backgroundColor: 'var(--orange, rgb(255,144,21))',
                                    '&:hover': { backgroundColor: '#e07f0d', boxShadow: 'none' }
                                }}
                            >
                                <Langs str={primaryActionKey} />
                            </Button>
                        </Box>

                        {dueDate ? (
                            <Box sx={{
                                mt: 1, display: 'flex', alignItems: 'center', gap: 0.5,
                                color: overdue ? 'var(--red, rgb(251,105,98))' : 'var(--text-muted, #8b92a0)'
                            }}>
                                <EventOutlinedIcon sx={{ fontSize: 14 }} aria-hidden="true" />
                                <Typography sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: overdue ? 600 : 400,
                                    color: 'inherit'
                                }}>
                                    <Langs
                                        str={overdue ? 'VLEARN_OVERDUE_SINCE' : 'VLEARN_DUE_ON'}
                                        params={{ date: moment(dueDate).format('DD/MM/YYYY') }}
                                    />
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                ) : null}

                {isManager ? (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 1, flexWrap: 'wrap',
                        ...(isLearner ? { mt: 1.25, pt: 1.25, borderTop: '1px solid rgba(0,0,0,0.07)' } : {})
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                size="small"
                                label={<Langs str={COURSE_STATUS_KEYS[course.status] || 'VLEARN_COURSE_STATUS_DRAFT'} />}
                                sx={{
                                    height: 22, borderRadius: '7px', fontSize: '0.72rem', fontWeight: 600,
                                    ...(COURSE_STATUS_STYLES[course.status] || COURSE_STATUS_STYLES.draft)
                                }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'var(--text-muted, #8b92a0)' }}>
                                <GroupOutlinedIcon sx={{ fontSize: 15 }} aria-hidden="true" />
                                <Typography sx={{ ...vlearnType.meta }}>
                                    <Langs
                                        str={enrolled === 1 ? 'VLEARN_N_ENROLLED_ONE' : 'VLEARN_N_ENROLLED_MANY'}
                                        params={{ count: enrolled }}
                                    />
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                            onClick={(event) => { event.stopPropagation(); if (onEdit) onEdit(course.id) }}
                            sx={{
                                ...focusRing, textTransform: 'none', fontSize: '0.78rem',
                                minHeight: 44,
                                borderColor: 'rgba(0,0,0,0.18)', color: 'var(--text-primary, #111318)'
                            }}
                        >
                            <Langs str="VLEARN_EDIT" />
                        </Button>
                    </Box>
                ) : null}
            </Box>
        </Box>
    )
}

/** The separator between two pieces of card metadata. Decorative, so hidden from the reader. */
function Dot() {
    return (
        <Box aria-hidden="true" sx={{
            width: 3, height: 3, borderRadius: '50%',
            backgroundColor: 'var(--text-muted, #8b92a0)', opacity: 0.6
        }} />
    )
}
